import { Box, Button, Link as MuiLink, MenuItem, TextField, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  BulkActionBar,
  BulkButton,
  BulkCheckbox,
  useBulkSelection,
} from "../../components/bulk-selection/index.js";
import { BackButton } from "@/components/ui/back-button";
import { PageLoader } from "@/components/ui/page-loader";
import { BulkDeleteDialog } from "../../components/ui/bulk-delete-dialog.js";
import { Pagination } from "../../components/ui/pagination.js";
import { usePagedList } from "../../hooks/usePagedList.js";
import { bulkDeleteDocs, listAllDocs, listCategories } from "./api.js";

/** Bulk delete: pick a category, select some/all of its documents (across every
 * release), and delete them in one confirmed action. The companion to the bulk
 * uploader — clear a folder before re-loading it. Reuses the shared bulk
 * selection + BulkDeleteDialog, and `bulkDeleteDocs` (which purges each doc's
 * attachments from object storage + its vector entry). Gated on
 * `documentation:edit`. */
export function DocumentBulkDeletePage(): JSX.Element {
  const qc = useQueryClient();
  const [categoryId, setCategoryId] = useState("");
  const [confirmBulk, setConfirmBulk] = useState(false);

  const { data: cats } = useQuery({ queryKey: ["docs", "categories"], queryFn: listCategories });
  const {
    data: docs,
    isFetching,
  } = useQuery({
    queryKey: ["docs", "bulk-delete", categoryId],
    queryFn: () => listAllDocs(categoryId ? { category_id: categoryId } : {}),
    enabled: categoryId !== "",
  });

  const list = docs ?? [];
  const bulk = useBulkSelection(list);
  const paging = usePagedList(list, 25);

  const bulkRemove = useMutation({
    mutationFn: (ids: number[]) => bulkDeleteDocs(ids),
    onSuccess: () => {
      bulk.clear();
      setConfirmBulk(false);
      void qc.invalidateQueries({ queryKey: ["docs"] });
    },
  });

  const categoryName = (cats?.data ?? []).find((c) => String(c.id) === categoryId)?.name ?? null;
  const selectAll = (): void => list.forEach((d) => bulk.setChecked(d.id, true));

  return (
    <Box sx={{ display: "grid", gap: 2.5 }}>
      <Box component="header">
        <BackButton to="/documentation" label="Documentation" sx={{ mb: 1.5 }} />
        <Typography component="h2" sx={{ mt: 0.5, fontSize: "1.125rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
          Bulk delete documents
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.disabled" }}>
          Pick a category, select the documents to remove (or all of them), and
          delete in one go. Each delete purges the file from storage and its AI
          index entry — this can&apos;t be undone.{" "}
          <MuiLink component={RouterLink} to="/documentation/manage/new/bulk" underline="hover">
            Bulk upload →
          </MuiLink>
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
        <TextField
          select
          label="Category"
          value={categoryId}
          onChange={(e) => {
            setCategoryId(e.target.value);
            bulk.clear();
          }}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="">Uncategorized</MenuItem>
          {(cats?.data ?? []).map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        {categoryId !== "" && (
          <Button type="button" variant="outlined" color="inherit" size="small" onClick={selectAll} disabled={list.length === 0}>
            Select all {list.length || ""}
          </Button>
        )}
      </Box>

      <BulkActionBar count={bulk.count} onClear={bulk.clear}>
        <BulkButton variant="destructive" onClick={() => setConfirmBulk(true)}>
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "text-bottom" }} />
          Delete {bulk.count} {bulk.count === 1 ? "document" : "documents"}
        </BulkButton>
      </BulkActionBar>

      {categoryId === "" ? null : isFetching && list.length === 0 ? (
        <PageLoader />
      ) : list.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
          No documents in {categoryName ?? "this category"}.
        </Typography>
      ) : (
        <Box data-testid="bulk-delete-list" sx={{ display: "grid", gap: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 0.75, borderRadius: 1, bgcolor: "surfaceSoft" }}>
            <BulkCheckbox
              checked={bulk.allOnPageChecked}
              indeterminate={bulk.someOnPageChecked}
              onChange={(checked) => bulk.toggleAllOnPage(checked)}
              ariaLabel="Select all documents on this page"
            />
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {bulk.count > 0 ? `${bulk.count} selected` : `${list.length} document${list.length === 1 ? "" : "s"}`}
              {categoryName ? ` in ${categoryName}` : ""}
            </Typography>
          </Box>
          {paging.paged.map((d) => (
            <Box
              key={d.id}
              data-testid="bulk-delete-row"
              sx={{ display: "flex", alignItems: "center", gap: 1.5, minHeight: 44, px: 1.5, py: 0.75, borderRadius: 1, border: 1, borderColor: "divider" }}
            >
              <BulkCheckbox
                checked={bulk.isSelected(d.id)}
                onChange={(checked, opts) => bulk.setChecked(d.id, checked, opts)}
                ariaLabel={`Select ${d.title}`}
              />
              <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14 }}>
                {d.title}
              </Box>
              {d.status === "published" && (
                <Box component="span" sx={{ flexShrink: 0, fontSize: "11px", color: "success.main" }}>published</Box>
              )}
            </Box>
          ))}
          <Pagination
            page={paging.page}
            perPage={paging.perPage}
            totalCount={paging.totalCount}
            totalPages={paging.totalPages}
            onPageChange={paging.setPage}
            hideIfSinglePage
          />
        </Box>
      )}

      <BulkDeleteDialog
        isOpen={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        items={list.filter((d) => bulk.isSelected(d.id)).map((d) => ({ id: d.id, title: d.title, status: d.status }))}
        entityLabel={{ singular: "Document", plural: "Documents" }}
        cascadingWarning="Each document's attachments will be purged from object storage and its vector-DB entry removed. This cannot be undone."
        renderItem={(it) => <>{it.title}</>}
        onConfirm={async () => {
          await bulkRemove.mutateAsync([...bulk.selected]);
        }}
      />
    </Box>
  );
}
